import React, { useMemo } from 'react';
import { addToast } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import ProductForm from '../../components/admin/products/ProductForm';
import { useProduct, useCreateProduct, useUpdateProduct } from '@/hooks/queries/useProducts';
import { ProductFormData } from '../../components/admin/products/schemas';
import { CreateProductRequest, UpdateProductRequest } from '@/services/apis/products';

interface ProductFormContainerProps {
  productId?: string | null;
  onSuccess: () => void;
  onCancel?: () => void;
}

const ProductFormContainer: React.FC<ProductFormContainerProps> = ({ productId, onSuccess, onCancel }) => {
  const { t } = useTranslation('admin');

  const { data: product, isLoading: isLoadingProduct } = useProduct({
    id: productId || '',
    shouldSkip: !productId,
  });

  const createProductMutation = useCreateProduct();
  const updateProductMutation = useUpdateProduct();

  const defaultValues = useMemo<Partial<ProductFormData> | undefined>(() => {
    if (!product || isLoadingProduct) return undefined;

    return {
      name: product.name || '',
      tagline: product.tagline || '',
      description: product.description || '',
      categoryId: product.category?.id || '',
      status: product.status || 'operational',
      icon: product.icon || '',
      url: product.url || '',
      features: product.features || [],
      targetUsers: product.targetUsers || [],
      slackChannels: product.slackChannels || [],
      contactPerson: product.contactPerson || { name: '', email: '', role: '' },
      metrics: product.metrics || {},
    };
  }, [product, isLoadingProduct]);

  const handleSubmit = async (data: ProductFormData) => {
    try {
      // Clean up data: convert empty strings to undefined for optional fields
      // Ensure arrays are always included (even if empty) so they can be properly saved/cleared
      const cleanedData = {
        ...data,
        categoryId: data.categoryId && data.categoryId.trim() !== '' ? data.categoryId : undefined,
        tagline: data.tagline && data.tagline.trim() !== '' ? data.tagline : undefined,
        description: data.description && data.description.trim() !== '' ? data.description : undefined,
        icon: data.icon && data.icon.trim() !== '' ? data.icon : undefined,
        url: data.url && data.url.trim() !== '' ? data.url : undefined,
        // Ensure arrays are always included (use empty array if undefined)
        features: data.features || [],
        targetUsers: data.targetUsers || [],
        slackChannels: data.slackChannels || [],
      };

      if (productId) {
        await updateProductMutation.mutateAsync({ id: productId, data: cleanedData as UpdateProductRequest });
        addToast({
          title: t('products.actions.updateSuccess'),
          color: 'success',
        });
      } else {
        await createProductMutation.mutateAsync(cleanedData as CreateProductRequest);
        addToast({
          title: t('products.actions.createSuccess'),
          color: 'success',
        });
      }
      onSuccess();
    } catch (error) {
      addToast({
        title: productId ? t('products.actions.updateError') : t('products.actions.createError'),
        color: 'danger',
      });
    }
  };

  const isSubmitting = createProductMutation.isPending || updateProductMutation.isPending;

  return (
    <ProductForm
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      isLoadingProduct={isLoadingProduct}
    />
  );
};

export default ProductFormContainer;
